import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useLoadingToast } from "../hooks/toast/toasts";
import CompleteLogo from "../media/logo/CompleteLogoBlue.png";
import { requestPasswordReset } from "../services/api/accounts/accountsApi";

// Cooldown de 60 segundos entre solicitações
const COOLDOWN_SECONDS = 60;
const STORAGE_KEY = "forgotPasswordCooldown";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Verificar cooldown ao carregar a página
  useEffect(() => {
    const storedCooldown = localStorage.getItem(STORAGE_KEY);
    if (storedCooldown) {
      const cooldownEnd = parseInt(storedCooldown, 10);
      const remaining = Math.ceil((cooldownEnd - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldownRemaining(remaining);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setInterval(() => {
        setCooldownRemaining((prev) => {
          if (prev <= 1) {
            localStorage.removeItem(STORAGE_KEY);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownRemaining]);

  const startCooldown = useCallback(() => {
    const cooldownEnd = Date.now() + COOLDOWN_SECONDS * 1000;
    localStorage.setItem(STORAGE_KEY, cooldownEnd.toString());
    setCooldownRemaining(COOLDOWN_SECONDS);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let toastId;

    // Verificar cooldown
    if (cooldownRemaining > 0) {
      toastId = initializeLoadingToast({
        text: `Aguarde ${formatTime(cooldownRemaining)} para solicitar novamente`,
      });
      updateLoadingToast({
        id: toastId,
        text: `Aguarde ${formatTime(cooldownRemaining)} para solicitar novamente`,
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      toastId = initializeLoadingToast({
        text: "Enviando email de recuperação...",
      });

      if (email === "") {
        throw new Error("Email é obrigatório");
      }

      // Validação básica de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error("Email inválido");
      }

      await requestPasswordReset(email);

      // Iniciar cooldown após sucesso
      startCooldown();
      setEmailSent(true);

      updateLoadingToast({
        id: toastId,
        text: "Email enviado com sucesso!",
        type: "success",
      });

    } catch (error) {
      // Se for rate limit do backend, iniciar cooldown também
      if (error.response?.status === 429) {
        startCooldown();
      }
      
      updateLoadingToast({
        id: toastId,
        text: error.response?.data?.error || error.message || "Erro ao solicitar recuperação de senha",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = loading || cooldownRemaining > 0;

  return (
    <Box
      display="flex"
      width="100vw"
      height="100vh"
      sx={{
        backgroundColor: "#1D1F2D",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(202, 153, 0, 0.15) 0%, transparent 70%)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        display="flex"
        width="90%"
        maxWidth="450px"
        sx={{
          borderRadius: 4,
          backgroundColor: "#ffffff",
          flexDirection: "column",
          alignItems: "center",
          p: 5,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <img
          width="55%"
          style={{ marginBottom: 24 }}
          src={CompleteLogo}
          alt="Logo JBE Engenharia"
        />

        {/* Divisor */}
        <Box
          sx={{
            width: "60%",
            height: "1px",
            background: "linear-gradient(to right, transparent, #e5e7eb, transparent)",
            mb: 3,
          }}
        />

        {/* Ícone de status - só mostra quando email foi enviado */}
        {emailSent && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: "#fff" }} />
          </Box>
        )}

        <Typography
          variant="h5"
          sx={{
            color: "#1e293b",
            fontWeight: 700,
            mb: 0.5,
            textAlign: "center",
          }}
        >
          {emailSent ? "Email enviado!" : "Esqueceu a senha?"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            mb: 4,
            textAlign: "center",
            px: 2,
            lineHeight: 1.6,
          }}
        >
          {emailSent 
            ? "Verifique sua caixa de entrada e spam. O link expira em 1 hora."
            : "Digite seu email cadastrado para receber o link de recuperação"
          }
        </Typography>

        <ToastContainer />

        {!emailSent ? (
          <form style={{ width: "100%" }} onSubmit={handleSubmit}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              E-mail
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isButtonDisabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#CA9900",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#CA9900",
                    borderWidth: 2,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.5,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isButtonDisabled}
              sx={{
                backgroundColor: isButtonDisabled ? "#e5e7eb" : "#CA9900",
                color: isButtonDisabled ? "#9ca3af" : "#fff",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                boxShadow: isButtonDisabled ? "none" : "0 4px 14px rgba(202, 153, 0, 0.4)",
                "&:hover": {
                  backgroundColor: isButtonDisabled ? "#e5e7eb" : "#b38800",
                  boxShadow: isButtonDisabled ? "none" : "0 6px 20px rgba(202, 153, 0, 0.5)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : cooldownRemaining > 0 ? (
                `Aguarde ${formatTime(cooldownRemaining)}`
              ) : (
                "Enviar link de recuperação"
              )}
            </Button>

            {cooldownRemaining > 0 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: "block",
                  textAlign: "center", 
                  color: "#9ca3af",
                  mt: 2
                }}
              >
                Você pode solicitar novamente em {formatTime(cooldownRemaining)}
              </Typography>
            )}
          </form>
        ) : (
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setEmailSent(false);
                setEmail("");
              }}
              disabled={cooldownRemaining > 0}
              sx={{ 
                borderColor: cooldownRemaining > 0 ? "#e5e7eb" : "#CA9900",
                color: cooldownRemaining > 0 ? "#9ca3af" : "#CA9900",
                py: 1.5,
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#b38800",
                  backgroundColor: "rgba(202, 153, 0, 0.05)",
                },
              }}
            >
              {cooldownRemaining > 0 
                ? `Reenviar em ${formatTime(cooldownRemaining)}`
                : "Enviar para outro email"
              }
            </Button>
          </Box>
        )}

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid #e5e7eb",
            width: "100%",
            textAlign: "center",
          }}
        >
          <NavLink
            to="/login"
            style={{
              textDecoration: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 500,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Voltar para login
          </NavLink>
        </Box>
      </Box>
    </Box>
  );
}
